import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

type ClockMode = 'hours' | 'minutes';

@Component({
  standalone: true,
  selector: 'app-time-picker',
  imports: [CommonModule, FormsModule],
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimePickerComponent),
    multi: true
  }]
})
export class TimePickerComponent implements OnDestroy, ControlValueAccessor {
  @Input() label = 'Time';
  @Input() required = false;

  hours = 9;
  minutes = 0;
  period: 'AM' | 'PM' = 'AM';
  mode: ClockMode = 'hours';
  open = false;
  disabled = false;

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};
  private _docClick: any;

  constructor() {
    this._docClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest('.np-time-picker');
      if (!el) this.close();
    };
    document.addEventListener('click', this._docClick);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._docClick);
  }

  writeValue(val: string | null): void {
    if (!val) return;
    const parts = val.split(':');
    let h = parseInt(parts[0], 10);
    const m = parseInt(parts[1] ?? '0', 10);
    this.period = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    this.hours = h;
    this.minutes = m;
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled = d; }

  get displayValue(): string {
    if (!this.hasValue) return '';
    return `${String(this.hours).padStart(2,'0')}:${String(this.minutes).padStart(2,'0')} ${this.period}`;
  }

  get hasValue(): boolean { return this.hours > 0 || this.minutes > 0; }

  toggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (this.open) this.mode = 'hours';
  }

  close(): void { this.open = false; }

  selectHour(h: number): void {
    this.hours = h;
    this.mode = 'minutes';
  }

  selectMinute(m: number): void {
    this.minutes = m;
    this.emit();
    setTimeout(() => this.close(), 150);
  }

  togglePeriod(p: 'AM' | 'PM'): void {
    this.period = p;
    this.emit();
  }

  get needleAngle(): number {
    if (this.mode === 'hours') return ((this.hours % 12) / 12) * 360;
    return (this.minutes / 60) * 360;
  }

  get clockNumbers(): { label: string; value: number; x: number; y: number }[] {
    const count = this.mode === 'hours' ? 12 : 12;
    const step  = this.mode === 'hours' ? 1  : 5;
    const r = 76;
    return Array.from({ length: count }, (_, i) => {
      const num   = i === 0 ? (this.mode === 'hours' ? 12 : 0) : (i * step);
      const angle = ((i / count) * 360 - 90) * (Math.PI / 180);
      return { label: String(num).padStart(2, '0'), value: num, x: 96 + r * Math.cos(angle), y: 96 + r * Math.sin(angle) };
    });
  }

  isActive(num: { value: number }): boolean {
    return this.mode === 'hours' ? this.hours === num.value : this.minutes === num.value;
  }

  get tipX(): number { return 96 + 76 * Math.cos((this.needleAngle - 90) * Math.PI / 180); }
  get tipY(): number { return 96 + 76 * Math.sin((this.needleAngle - 90) * Math.PI / 180); }

  adjustHour(delta: number): void {
    this.hours = ((this.hours - 1 + delta + 12) % 12) + 1;
    this.emit();
  }

  adjustMinute(delta: number): void {
    this.minutes = (this.minutes + delta + 60) % 60;
    this.emit();
  }

  emit(): void {
    let h24 = this.hours % 12;
    if (this.period === 'PM') h24 += 12;
    const str = `${String(h24).padStart(2,'0')}:${String(this.minutes).padStart(2,'0')}`;
    this.onChange(str);
    this.onTouched();
  }
}
